import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AsYouType, type CountryCode, parsePhoneNumber } from 'libphonenumber-js';
import { Subject } from 'rxjs';

import { MatInput } from '@angular/material/input';
import { ALL_COUNTRIES, CountryData } from './country-data';
import { PHONE_INPUT_CONFIG } from './phone-input.config';

const FALLBACK_COUNTRY: CountryCode = 'NL';

let nextId = 0;

@Component({
  selector: 'app-phone-input',
  imports: [
    FormsModule,
    MatDivider,
    MatIcon,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    NgClass,
  ],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatFormFieldControl, useExisting: PhoneInputComponent }],
  host: {
    '[class.phone-input-floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedByIds',
  },
})
export class PhoneInputComponent
  implements ControlValueAccessor, MatFormFieldControl<string>
{
  private readonly _focusMonitor = inject(FocusMonitor);
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly numberInputRef =
    viewChild<ElementRef<HTMLInputElement>>('numberInput');
  protected readonly searchInputRef =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');

  readonly ngControl = inject(NgControl, { optional: true, self: true });

  readonly id = `app-phone-input-${nextId++}`;
  readonly controlType = 'app-phone-input';
  readonly stateChanges = new Subject<void>();
  readonly autofilled = false;
  readonly userAriaDescribedBy = '';

  private readonly _config = inject(PHONE_INPUT_CONFIG, { optional: true });

  // --- Public inputs ---
  readonly inputId = input<string>('');
  readonly searchable = input(false, { transform: booleanAttribute });
  readonly preferredCountries = input<CountryCode[]>([]);
  readonly onlyCountries = input<CountryCode[]>([]);
  readonly formatAsYouType = input<boolean | undefined>(undefined);

  /** Resolved default country: instance input > global config > 'NL' */
  private readonly _defaultCountry: CountryCode =
    this._config?.defaultCountry ?? FALLBACK_COUNTRY;

  /** Whether as-you-type formatting is enabled: instance input > global config > true */
  protected readonly isFormatEnabled = computed(() => {
    const instanceValue = this.formatAsYouType();
    if (instanceValue !== undefined) return instanceValue;
    return this._config?.formatAsYouType ?? true;
  });

  // --- Internal state ---
  protected readonly selectedCountryCode = signal<CountryCode>(this._defaultCountry);
  protected readonly localNumber = signal<string>('');
  protected readonly searchQuery = signal<string>('');

  /** Filtered base list: instance input > global config > all countries */
  protected readonly availableCountries = computed(() => {
    const only = this.onlyCountries();
    const resolved = only.length > 0 ? only : (this._config?.onlyCountries ?? []);
    if (resolved.length > 0) {
      return ALL_COUNTRIES.filter((c) => resolved.includes(c.code));
    }
    return ALL_COUNTRIES;
  });

  /** Preferred countries: instance input > global config > none */
  protected readonly preferredList = computed(() => {
    const preferred = this.preferredCountries();
    const resolved =
      preferred.length > 0 ? preferred : (this._config?.preferredCountries ?? []);
    if (resolved.length === 0) return [];
    return resolved
      .map((code) => this.availableCountries().find((c) => c.code === code))
      .filter((c): c is CountryData => c != null);
  });

  /** Non-preferred countries from the available list */
  protected readonly otherList = computed(() => {
    const preferred = this.preferredCountries();
    const resolved =
      preferred.length > 0 ? preferred : (this._config?.preferredCountries ?? []);
    if (resolved.length === 0) return this.availableCountries();
    return this.availableCountries().filter((c) => !resolved.includes(c.code));
  });

  /** Filtered preferred list for searchable mode */
  protected readonly filteredPreferredList = computed(() => {
    const query = this.searchQuery().toLowerCase();
    if (!query) return this.preferredList();
    return this.preferredList().filter(
      (c) => c.name.toLowerCase().includes(query) || c.dialCode.includes(query)
    );
  });

  /** Filtered other list for searchable mode */
  protected readonly filteredOtherList = computed(() => {
    const query = this.searchQuery().toLowerCase();
    if (!query) return this.otherList();
    return this.otherList().filter(
      (c) => c.name.toLowerCase().includes(query) || c.dialCode.includes(query)
    );
  });

  /** Whether to show divider (both groups have results) */
  protected readonly showFilteredDivider = computed(
    () => this.filteredPreferredList().length > 0 && this.filteredOtherList().length > 0
  );

  /** Whether the country selector should be interactive (more than 1 country) */
  protected readonly hasMultipleCountries = computed(
    () => this.availableCountries().length > 1
  );

  /** Currently selected country data (falls back to first available country) */
  protected readonly selectedCountry = computed(() => {
    const available = this.availableCountries();
    return available.find((c) => c.code === this.selectedCountryCode()) ?? available[0];
  });

  private readonly _focused = signal(false);
  private readonly _disabled = signal(false);
  private readonly _required = signal(false);
  private readonly _placeholder = signal('');

  protected describedByIds = '';

  private _onChange: ((value: string) => void) | null = null;
  private _onTouched: (() => void) | null = null;

  get value(): string {
    return this._buildE164();
  }

  set value(val: string) {
    this.writeValue(val);
    this.stateChanges.next();
  }

  get placeholder(): string {
    return this._placeholder();
  }

  set placeholder(val: string) {
    this._placeholder.set(val);
    this.stateChanges.next();
  }

  get focused(): boolean {
    return this._focused();
  }

  get empty(): boolean {
    return !this.localNumber().trim();
  }

  readonly shouldLabelFloat = true;

  get required(): boolean {
    return this._required();
  }

  set required(val: boolean) {
    this._required.set(coerceBooleanProperty(val));
    this.stateChanges.next();
  }

  get disabled(): boolean {
    return this._disabled();
  }

  set disabled(val: boolean) {
    this._disabled.set(coerceBooleanProperty(val));
    this.stateChanges.next();
  }

  get errorState(): boolean {
    const control = this.ngControl?.control;
    return !!(control?.invalid && (control?.touched || control?.dirty));
  }

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this._focusMonitor.monitor(this._elementRef, true).subscribe((origin) => {
      const isFocused = !!origin;
      if (!isFocused) {
        this._onTouched?.();
      }
      this._focused.set(isFocused);
      this.stateChanges.next();
    });

    this._destroyRef.onDestroy(() => {
      this.stateChanges.complete();
      this._focusMonitor.stopMonitoring(this._elementRef);
    });
  }

  // --- MatFormFieldControl ---

  setDescribedByIds(ids: string[]): void {
    this.describedByIds = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName !== 'INPUT') {
      this.numberInputRef()?.nativeElement.focus();
    }
  }

  // --- ControlValueAccessor ---

  writeValue(value: string | null): void {
    if (!value) {
      this.selectedCountryCode.set(this._safeDefaultCountry());
      this.localNumber.set('');
      return;
    }

    try {
      const parsed = parsePhoneNumber(value);
      if (
        parsed?.country &&
        this.availableCountries().some((c) => c.code === parsed.country)
      ) {
        this.selectedCountryCode.set(parsed.country);
        const national = parsed.nationalNumber as string;
        this.localNumber.set(
          this.isFormatEnabled()
            ? this._formatNational(national, parsed.country)
            : national
        );
      } else {
        this.selectedCountryCode.set(this._safeDefaultCountry());
        this.localNumber.set(value);
      }
    } catch {
      this.selectedCountryCode.set(this._safeDefaultCountry());
      this.localNumber.set(value);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Event handlers ---

  protected handleCountryChange(countryCode: CountryCode): void {
    this.selectedCountryCode.set(countryCode);
    if (this.isFormatEnabled() && this.localNumber()) {
      this.localNumber.set(this._formatNational(this.localNumber(), countryCode));
    }
    this._emitValue();
  }

  protected handleMenuOpened(): void {
    if (this.searchable()) {
      setTimeout(() => this.searchInputRef()?.nativeElement.focus());
    }
  }

  protected handleMenuClosed(): void {
    this.searchQuery.set('');
  }

  protected handleSearchInput(value: string): void {
    this.searchQuery.set(value);
  }

  protected handleNumberInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const rawValue = inputEl.value;

    if (!this.isFormatEnabled()) {
      this.localNumber.set(rawValue);
      this._emitValue();
      return;
    }

    const cursorPos = inputEl.selectionStart ?? rawValue.length;

    // Count digits before cursor in the raw (pre-format) value
    const digitsBefore = rawValue.slice(0, cursorPos).replace(/\D/g, '').length;

    const formatted = this._formatNational(rawValue, this.selectedCountryCode());
    this.localNumber.set(formatted);
    inputEl.value = formatted;

    // Restore cursor: find position after the same number of digits,
    // then skip past any trailing formatting characters (e.g. ")", " ", "-")
    // so the cursor lands at the next typing position.
    let digitCount = 0;
    let newCursorPos = formatted.length;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitCount++;
      }
      if (digitCount === digitsBefore) {
        // Skip past any non-digit formatting chars that follow this digit
        let end = i + 1;
        while (end < formatted.length && !/\d/.test(formatted[end])) {
          end++;
        }
        newCursorPos = end;
        break;
      }
    }

    inputEl.setSelectionRange(newCursorPos, newCursorPos);
    this._emitValue();
  }

  protected handleBlur(): void {
    this._onTouched?.();
  }

  protected formatOptionLabel(country: CountryData): string {
    return `${country.name} +${country.dialCode}`;
  }

  // --- Private helpers ---

  /**
   * Format a local number using AsYouType for the given country.
   * Prepends a leading '0' back if the formatter strips it (common in national formats).
   */
  private _formatNational(localValue: string, country: CountryCode): string {
    const digits = localValue.replace(/\D/g, '');
    if (!digits) return '';

    const formatter = new AsYouType(country);
    const result = formatter.input(digits);

    // AsYouType may strip the leading 0 (it's a trunk prefix).
    // If the user typed a leading 0 and the result doesn't start with one,
    // prepend it back since we're showing national format.
    if (digits.startsWith('0') && !result.startsWith('0')) {
      return `0${result}`;
    }

    return result;
  }

  /** Returns defaultCountry if it exists in available countries, otherwise first available */
  private _safeDefaultCountry(): CountryCode {
    const available = this.availableCountries();
    if (available.some((c) => c.code === this._defaultCountry)) {
      return this._defaultCountry;
    }
    return available[0]?.code ?? FALLBACK_COUNTRY;
  }

  private _buildE164(): string {
    const local = this.localNumber().trim();
    if (!local) {
      return '';
    }

    const dialCode = this.selectedCountry().dialCode;
    const digits = local.replace(/[\s\-()]/g, '');

    return digits.startsWith('0')
      ? `+${dialCode}${digits.slice(1)}`
      : `+${dialCode}${digits}`;
  }

  private _emitValue(): void {
    const e164 = this._buildE164();
    this._onChange?.(e164);
    this.stateChanges.next();
  }
}
